import { useEffect } from "react";

/**
 * Subscribe to 1D/2D BARCODE scans on the PDA.
 *
 * Handles TWO delivery modes that OEM PDA scanners commonly use:
 *
 *   1. Native CustomEvent — the DrrtPda WebView wrapper fires a
 *      `pda-barcode-scan` event with `detail.barcode` when the operator
 *      presses side-key 622 (KEY_LSCAN) and the OEM head decodes.
 *
 *   2. Keyboard-wedge — some PDAs (Chainway C72, some Zebra models)
 *      type the barcode as a burst of keystrokes terminated by Enter,
 *      bypassing the WebView bridge entirely. We distinguish wedge
 *      bursts from real typing by inter-key delay (wedges fire chars
 *      in < 20 ms; humans are 80 ms+). Bursts hitting an <input> or
 *      <textarea> are left alone so the operator can still type freely.
 */
export function usePdaBarcode(onBarcode, { enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;

    // ── Path 1 · Native CustomEvent (preferred) ──────────────────────
    const eventHandler = (e) => {
      const code = e?.detail?.barcode;
      if (code && typeof code === "string" && code.trim()) onBarcode(code.trim());
    };
    window.addEventListener("pda-barcode-scan", eventHandler);

    // ── Path 2 · Keyboard-wedge fallback ─────────────────────────────
    let buf = "";
    let lastKeyAt = 0;
    let bufferedFast = false;  // was the currently-accumulating buffer built quickly (i.e. from a scanner burst)?
    const keyHandler = (e) => {
      // Don't hijack real typing into form fields.
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      const now = Date.now();
      const gap = now - lastKeyAt;
      // Reset the buffer when the previous key was more than 120 ms ago
      // (human keystroke, not a burst).
      if (gap > 120) {
        buf = "";
        bufferedFast = true; // fresh burst starts; will be invalidated by any slow key
      }
      lastKeyAt = now;
      if (e.key === "Enter") {
        const code = buf.trim();
        buf = "";
        // Only fire if the buffer was assembled via fast bursts and looks
        // meaningfully long — avoids catching stray Enter key presses.
        if (bufferedFast && code.length >= 4) {
          e.preventDefault();
          onBarcode(code);
        }
        return;
      }
      // A single key that took >120 ms to arrive isn't part of a wedge
      // burst — flag the buffer as tainted so its Enter won't be treated
      // as a scan.
      if (gap > 120 && buf.length > 0) bufferedFast = false;
      if (e.key && e.key.length === 1) buf += e.key;
    };
    window.addEventListener("keydown", keyHandler);

    return () => {
      window.removeEventListener("pda-barcode-scan", eventHandler);
      window.removeEventListener("keydown", keyHandler);
    };
  }, [enabled, onBarcode]);
}
